import { ActionIcon, Anchor, AppShell, Group, Image, Overlay } from '@mantine/core';
import { IconMenu2 } from '@tabler/icons-react';
import { useDisclosure, useWindowEvent } from '@mantine/hooks';
import { Outlet, createFileRoute, useLocation } from '@tanstack/react-router';
import { BeforeLoadContext, LoaderFnContext } from '@tanstack/react-router/dist/esm/route';

import { notifyError } from '@/lib/notification';
import { MyRouterContext } from '@/App';
import { ProjectSelector } from '@/components/ProjectSelector';
import { UserMenu } from '@/components/UserMenu/UserMenu';
import { FullScreenLoading } from '@/components/FullScreenLoading';
import { LS_KEY_CURRENT_PROJECT } from '@/const';
import { CSSProperties, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

export const Route = createFileRoute('/_main')({
  validateSearch: z.object({ project: z.string().optional() }),
  // loaderdeps is needed since loader does not depend on the context updated by beforeLoad, so it won't reloaded without loaderDeps.
  loaderDeps: ({ search: { project } }) => ({ project }),
  beforeLoad,
  loader,
  // NOTE onError is needed to avoid suppressing errors of beforeLoad
  onError: (error: Error) => notifyError(error),
  component: Component,
  pendingComponent: () => <FullScreenLoading />,
  shouldReload: false,
});

async function beforeLoad({
  search,
  context,
}: BeforeLoadContext<{ project?: string }, Record<never, string>, MyRouterContext>) {
  let newProjectId = search.project ?? null;
  const storedProject = localStorage.getItem(LS_KEY_CURRENT_PROJECT);
  // If project is given in search, use it and store it.
  if (newProjectId && newProjectId !== storedProject) {
    localStorage.setItem(LS_KEY_CURRENT_PROJECT, newProjectId);
  }
  // If project is not given in search and stored project is available, use stored project.
  if (!newProjectId && storedProject) {
    newProjectId = storedProject;
  }
  context.projectId = newProjectId;

  return context;
}

async function loader({
  deps: { project },
  context: { auth, userInfo, adminClient, projectId },
}: LoaderFnContext<Record<never, string>, { project: string | undefined }, MyRouterContext>) {
  return {
    auth,
    userInfo,
    // honor the value from search query, then use the value provided by beforeLoad
    projectId: project ?? projectId,
    adminClient,
  };
}

function Component() {
  const { auth, userInfo, projectId, adminClient } = Route.useLoaderData();
  const [opened, { toggle }] = useDisclosure();
  const pathname = useLocation({
    select: (location) => location.pathname,
  });
  useEffect(() => {
    opened && toggle();
  }, [pathname]);
  // 開いたナビは Esc でも閉じる（スクリムのタップと同じ逃げ道をキーボードにも用意する）
  useWindowEvent('keydown', (event) => {
    if (event.key === 'Escape' && opened) {
      toggle();
    }
  });

  const tenantQuery = useQuery({
    queryKey: ['tenant'],
    queryFn: () => adminClient.settings_v1_get_tenant(),
  });

  return (
    <AppShell
      header={{ height: 48 }}
      navbar={{
        width: 240,
        breakpoint: 'md',
        collapsed: { mobile: !opened },
      }}
      // ナビの出入りは chat_ui のサイドバーと同じ速さ・曲線にする
      transitionDuration={350}
      transitionTimingFunction="cubic-bezier(0.2, 0, 0, 1)"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group justify="start" gap="xs" wrap="nowrap">
            {/* chat_ui のヘッダーと同じ、面の色を敷いた 40px の丸にハンバーガー */}
            <ActionIcon
              onClick={toggle}
              hiddenFrom="md"
              variant="filled"
              radius="xl"
              size={40}
              aria-label="メニューを開閉する"
              aria-expanded={opened}
              style={
                {
                  '--ai-bg': 'var(--plamo-muted)',
                  '--ai-hover': 'var(--mantine-color-default-hover)',
                  '--ai-color': 'var(--mantine-color-text)',
                } as CSSProperties
              }
            >
              <IconMenu2 size={20} />
            </ActionIcon>
            {/* 狭い幅ではブランドはナビの最上部（NavbarBrand）に出す */}
            <Anchor href="/" visibleFrom="md">
              <Image src="/plm_a_full_rgb.png" w="100" h="32" alt="plamo" />
            </Anchor>
          </Group>
          <Group justify="end" gap="xs" wrap="nowrap">
            {/* 選択中のプロジェクトは画面の文脈そのものなので、ナビを開かなくても見える
                ヘッダーに置く */}
            <ProjectSelector userInfo={userInfo} adminClient={adminClient} projectId={projectId} />
            <UserMenu logout={auth.logout} userInfo={userInfo} tenant={tenantQuery.data} />
          </Group>
        </Group>
      </AppShell.Header>

      {opened && (
        <Overlay
          fixed
          hiddenFrom="md"
          // AppShell の navbar / header（既定の z-index 100）より下に敷く
          zIndex={99}
          backgroundOpacity={0.4}
          onClick={toggle}
        />
      )}

      <Outlet />
    </AppShell>
  );
}
